import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import {SaveOutlined} from '@material-ui/icons';
import PageWrapper from '../../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../../shared/pagetitle/PageTitle.js';
import GenericTab from '../../../../shared/generictab/GenericTab.js';
import useConfirmationDialog from '../../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../../shared/toast/ToastProvider.tsx';
import PageNaoEncontrada from '../../../../PageNaoEncontrada.jsx';
import useFetch from '../../../../../hooks/useFetch.js';
import useCreate from '../../../../../hooks/useCreate.js';
import useUpdate from '../../../../../hooks/useUpdate.js';
import useCheckModule from '../../../../../hooks/useCheckModule.js';
import {containedButton, containerPaper, textButton} from '../../../../../utils/styles.js';
import {
    URL_PROCESSOS_SELECAO,
    URL_PROCESSOS_SELECAO_APOIO_CAMPI,
    URL_PROCESSOS_SELECAO_APOIO_CAMPI_REMETENTE,
    URL_PROCESSOS_SELECAO_APOIO_CURSOS,
    URL_PROCESSOS_SELECAO_APOIO_DOCUMENTOS,
    URL_PROCESSOS_SELECAO_APOIO_ENEMS,
    URL_PROCESSOS_SELECAO_APOIO_PROCESSOS_VESTIBULAR,
    URL_PROCESSOS_SELECAO_APOIO_PROGRAMAS,
    URL_PROCESSOS_SELECAO_APOIO_TIPOS_CALENDARIO,
    URL_PROCESSOS_SELECAO_APOIO_TURNOS,
    URL_PROCESSOS_SELECAO_ID,
    URL_PROCESSOS_SELECAO_NOVO
} from '../../../../../constants/index.js';
import {
    idsProcessosVestibular,
    montarEstadoInicial,
    montarPayload,
    processosAoTrocarPrograma,
    regrasDoPrograma,
    ROTA_PROCESSOS_SELECAO
} from '../../../components/processo/processoSelecao.js';
import {ABAS, abasComErro, criarSchemaProcessoSelecao, primeiraAbaComErro} from '../../../components/processo/processoSelecaoSchema.js';
import useCarregamentoPagina from '../../../components/useCarregamentoPagina.js';
import TabGeral from '../../../tabs/processo/TabGeral.jsx';
import TabInscricao from '../../../tabs/processo/TabInscricao.jsx';
import TabAgendamento from '../../../tabs/processo/TabAgendamento.jsx';
import TabCalendarios from '../../../tabs/processo/TabCalendarios.jsx';
import TabDocumentos from '../../../tabs/processo/TabDocumentos.jsx';
import TabEmail from '../../../tabs/processo/TabEmail.jsx';
import TabFormulario from '../../../tabs/processo/TabFormulario.jsx';
import TabFicha from '../../../tabs/processo/TabFicha.jsx';
import TabCarga from '../../../tabs/processo/TabCarga.jsx';

const PARAMS_VAZIOS = {};
const SCHEMA = criarSchemaProcessoSelecao();

const APOIO_VAZIO = {
    programas: [],
    campi: [],
    campiRemetente: [],
    turnos: [],
    enems: [],
    tiposCalendario: [],
    documentos: [],
    processosVestibular: []
};

/* Listas de apoio carregadas uma vez: [chave em APOIO_VAZIO, URL]. */
const URLS_APOIO = [
    ['programas', URL_PROCESSOS_SELECAO_APOIO_PROGRAMAS],
    ['campi', URL_PROCESSOS_SELECAO_APOIO_CAMPI],
    ['campiRemetente', URL_PROCESSOS_SELECAO_APOIO_CAMPI_REMETENTE],
    ['turnos', URL_PROCESSOS_SELECAO_APOIO_TURNOS],
    ['enems', URL_PROCESSOS_SELECAO_APOIO_ENEMS],
    ['tiposCalendario', URL_PROCESSOS_SELECAO_APOIO_TIPOS_CALENDARIO],
    ['documentos', URL_PROCESSOS_SELECAO_APOIO_DOCUMENTOS],
    ['processosVestibular', URL_PROCESSOS_SELECAO_APOIO_PROCESSOS_VESTIBULAR]
];

/* Abas verticais com as mesmas larguras das seções da inscrição do administrativo. */
const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    tabs: {
        width: '100%',
        minWidth: '180px',
        maxWidth: '220px',
        marginRight: theme.spacing(2),
        borderRight: `1px solid ${theme.palette.divider}`,
        '& .MuiTab-root': {
            '&.MuiButtonBase-root.MuiTab-root.MuiTab-textColorPrimary': {
                width: '100%',
                minWidth: '180px',
                maxWidth: '220px',
                alignItems: 'flex-start',
                textAlign: 'left'
            }
        }
    },
    actionsRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    ...containedButton(theme, false, false),
    ...textButton(theme, false)
}));

/*
 * Criar (/administrativo/cadastros/processos-selecao/novo) e editar
 * (/administrativo/cadastros/processos-selecao/:id) um processo de seleção —
 * migração do processoSeletivo/_form.xhtml do PrimeFaces, com as 9 abas dele.
 * A criação parte dos padrões do back (GET /novo). O Salvar valida tudo, marca
 * as abas com erro e abre a primeira; depois de gravar volta para a lista,
 * como o JSF. Enter num campo não grava: só o botão Salvar.
 */
export default function PageProcessoSelecao() {
    const classes = useStyles();
    const {id} = useParams();
    const edicao = !!id;
    const navigate = useNavigate();
    const checkModule = useCheckModule();
    const permitido = checkModule('PROCSEL');

    const [fetchProcesso, carregandoProcesso] = useFetch({returnInnerLoadingState: true});
    const [fetchApoio] = useFetch({returnInnerLoadingState: true});
    const [fetchCursos] = useFetch({returnInnerLoadingState: true});
    const create = useCreate();
    const update = useUpdate();
    const {createToast} = useToast();
    const confirmation = useConfirmationDialog();
    const tabRef = useRef();
    const requisicaoCursos = useRef(0);

    const [apoio, setApoio] = useState(APOIO_VAZIO);
    const [carregandoApoio, setCarregandoApoio] = useState(false);
    // Loading da página pelo LoadingModal global (o BlockUI fica só nas tabelas).
    useCarregamentoPagina(carregandoProcesso || carregandoApoio);
    const [cursos, setCursos] = useState([]);
    const [original, setOriginal] = useState(null);

    const {control, handleSubmit, watch, setValue, getValues, reset, formState: {errors, isSubmitting, isDirty}} = useForm({
        defaultValues: montarEstadoInicial({}),
        resolver: yupResolver(SCHEMA)
    });

    const idPrograma = watch('idPrograma');
    const regras = regrasDoPrograma(idPrograma);
    const idsProcessos = idsProcessosVestibular(watch('processosVestibular'));

    useEffect(() => {
        if (!permitido) return;
        setCarregandoApoio(true);
        Promise.all(URLS_APOIO.map(([, url]) => fetchApoio(url, PARAMS_VAZIOS)))
            .then(respostas => setApoio(URLS_APOIO.reduce((listas, [chave], indice) => ({
                ...listas,
                [chave]: Array.isArray(respostas[indice]) ? respostas[indice] : []
            }), {...APOIO_VAZIO})))
            .finally(() => setCarregandoApoio(false));
    }, []);

    /* Criação parte dos padrões do back; edição, do processo gravado. A troca de rota recomeça o formulário. */
    useEffect(() => {
        if (!permitido) return;
        setOriginal(null);
        reset(montarEstadoInicial({}));
        tabRef.current && tabRef.current.resetTabIndex();
        fetchProcesso(edicao ? URL_PROCESSOS_SELECAO_ID(id) : URL_PROCESSOS_SELECAO_NOVO, PARAMS_VAZIOS, vo => {
            setOriginal(vo || {});
            reset(montarEstadoInicial(vo || {}));
        });
    }, [id]);

    /* Cursos permitidos dependem do programa e dos processos vestibulares; respostas antigas são descartadas. */
    useEffect(() => {
        if (!permitido || !regras.exibirInscricao) {
            setCursos([]);
            return;
        }
        const requisicao = ++requisicaoCursos.current;
        const params = idsProcessos ? {idPrograma, idsProcessosVestibular: idsProcessos} : {idPrograma};
        fetchCursos(URL_PROCESSOS_SELECAO_APOIO_CURSOS, params, lista => {
            if (requisicao === requisicaoCursos.current) {
                setCursos(Array.isArray(lista) ? lista : []);
            }
        });
    }, [idPrograma, idsProcessos, regras.exibirInscricao]);

    /* Colégio Técnico aceita um só processo vestibular: ao trocar para ele, a lista com mais de um é limpa (como no JSF). */
    const trocarPrograma = novoPrograma => {
        const atuais = getValues('processosVestibular');
        const ajustados = processosAoTrocarPrograma(atuais, novoPrograma);
        if (ajustados !== atuais) {
            setValue('processosVestibular', ajustados, {shouldDirty: true});
        }
    };

    const voltarParaLista = () => navigate(ROTA_PROCESSOS_SELECAO);

    const salvar = valores => {
        const payload = montarPayload(valores, {programas: apoio.programas});
        const concluir = mensagem => () => {
            createToast({severity: 'success', message: mensagem});
            voltarParaLista();
        };
        return edicao
            ? update(URL_PROCESSOS_SELECAO_ID(id), payload, concluir('Processo de seleção atualizado com sucesso!'))
            : create(URL_PROCESSOS_SELECAO, payload, concluir('Processo de seleção criado com sucesso!'));
    };

    /* Salvar com erro: abre a primeira aba com erro (as demais ficam marcadas). */
    const aoInvalidar = erros => {
        const indice = primeiraAbaComErro(erros);
        if (indice >= 0 && tabRef.current) {
            tabRef.current.setTabIndex(indice);
        }
    };

    const cancelar = () => {
        if (!isDirty) {
            voltarParaLista();
            return;
        }
        confirmation('Há alterações não salvas. Deseja descartá-las?', voltarParaLista);
    };

    if (!permitido) {
        return <PageNaoEncontrada/>;
    }

    // Na edição o programa fica travado: garante a opção dele mesmo que o apoio não o traga.
    const programaOriginal = original?.programa;
    const programas = programaOriginal?.id && !apoio.programas.some(programa => String(programa.id) === String(programaOriginal.id))
        ? [programaOriginal, ...apoio.programas]
        : apoio.programas;

    const comErro = abasComErro(errors);
    const props = {control, errors, watch, setValue};

    const conteudoAba = aba => {
        switch (aba) {
            case 'inscricao':
                return (
                    <TabInscricao
                        {...props}
                        cursos={cursos}
                        campi={apoio.campi}
                        turnos={apoio.turnos}
                        enems={apoio.enems}
                        processosVestibularApoio={apoio.processosVestibular}/>
                );
            case 'agendamento':
                return <TabAgendamento control={control} errors={errors}/>;
            case 'calendarios':
                return <TabCalendarios watch={watch} setValue={setValue} tiposCalendario={apoio.tiposCalendario}/>;
            case 'documentos':
                return <TabDocumentos watch={watch} setValue={setValue} documentos={apoio.documentos}/>;
            case 'email':
                return <TabEmail control={control} errors={errors} campiRemetente={apoio.campiRemetente}/>;
            case 'formulario':
                return <TabFormulario watch={watch} setValue={setValue}/>;
            case 'ficha':
                return <TabFicha watch={watch} setValue={setValue}/>;
            case 'carga':
                return <TabCarga {...props}/>;
            default:
                return <TabGeral control={control} errors={errors} programas={programas} edicao={edicao} onTrocarPrograma={trocarPrograma}/>;
        }
    };

    const abas = ABAS.map(aba => ({...aba, hasError: comErro.has(aba.id), content: conteudoAba(aba.id)}));

    return (
        <PageWrapper>
            <PageTitle title={edicao ? 'Edição de Processo de Seleção' : 'Cadastro de Processo de Seleção'}/>
            <Paper className={classes.containerPaper}>
                <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                    {/* Salvar só pelo botão: Enter num campo não grava o processo inteiro. */}
                    <form noValidate autoComplete='off' onSubmit={evento => evento.preventDefault()}>
                        {!!original && (
                            <GenericTab
                                ref={tabRef}
                                orientation='vertical'
                                mobileVariant='accordion'
                                noAppBar
                                indicatorColor='primary'
                                textColor='primary'
                                scrollButtons='auto'
                                className={classes.tabs}
                                content={abas}/>
                        )}
                        <Divider/>
                        <Grid container>
                            <Grid item xs={12} className={classes.actionsRow}>
                                <Button className={classes.textButton} onClick={cancelar}>Cancelar</Button>
                                <Button variant='contained' startIcon={<SaveOutlined/>} className={classes.containedButton}
                                        onClick={handleSubmit(salvar, aoInvalidar)} disabled={isSubmitting || !original}>
                                    Salvar
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </MuiPickersUtilsProvider>
            </Paper>
        </PageWrapper>
    );
}
